import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

import { FeaturesRoutingModule } from './features-routing.module';

import { SharedModule } from '../shared/shared.module';
import { SharedPrimengModule } from '../shared/shared-primeng.module';
import { ConfirmationService } from 'primeng/api';
import { PredictiveMaintenanceComponent } from '../shared/components/predictive-maintenance/predictive-maintenance.component';
import {
  BaseChartDirective,
  provideCharts,
  withDefaultRegisterables,
} from 'ng2-charts';
import { RulesOverviewComponent } from './factory-summary/rules-overview/rules-overview.component';
import { AlarmDetailsComponent } from './alarm-details/alarm-details.component';
import { SedEventsSummaryComponent } from './sed-events-summary/sed-events-summary.component';
import { SedEventsTableComponent } from './sed-events-summary/sed-events-table/sed-events-table.component';
import { SedEventsDistributionComponent } from './sed-events-summary/sed-events-distribution/sed-events-distribution.component';
import { SedEventsPredictiveMaintenanceComponent } from './sed-events-summary/sed-events-predictive-maintenance/sed-events-predictive-maintenance.component';

import { SupportComponent } from './support/support.component';
import { WelcomePageComponent } from './welcome-page/welcome-page.component';
import { FloatingPathsComponent } from './welcome-page/floating-paths/floating-paths.component';
import { IconsModule } from '../shared/icons.module';
import { EvaluationFormComponent } from './alarm-details/evaluation-form/evaluation-form.component';
import { DescriptionComponent } from './alarm-details/description/description.component';
import { CoordinatorComponent } from './coordinator/coordinator.component';
import { NewModelComponent } from './coordinator/operational-view/new-model/new-model.component';
import { EquipmentStructureImportComponent } from './coordinator/equipment-structure-import/equipment-structure-import.component';
import { MigrationImportComponent } from './coordinator/migration-import/migration-import.component';
import { GainParametersComponent } from './coordinator/gain-parameters/gain-parameters.component';
import { GainFormulaTemplatesComponent } from './coordinator/gain-formula-templates/gain-formula-templates.component';
import { FactorySummaryComponent } from './factory-summary/factory-summary.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ModifyRuleComponent } from '../shared/components/modify-rule/modify-rule.component';
import { LocalisationStepComponent } from '../shared/components/modify-rule/steps/localisation-step/localisation-step.component';
import { ParametersStepComponent } from '../shared/components/modify-rule/steps/parameters-step/parameters-step.component';
import { EquationsStepComponent } from '../shared/components/modify-rule/steps/equations-step/equations-step.component';
import { GainFormulaInstancesStepComponent } from '../shared/components/modify-rule/steps/gain-formula-instances-step/gain-formula-instances-step.component';
import { ThresholdsComponent } from '../shared/components/modify-rule/steps/thresholds/thresholds.component';
import { DataStateComponent } from '../shared/components/data-state/data-state.component';
import { PotentialCauseComponent } from './alarm-details/potential-cause/potential-cause.component';
import { EditPotentialCausesModalComponent } from './alarm-details/potential-cause/edit-potential-causes-modal/edit-potential-causes-modal.component';
import { EditCorrectiveActionsModalComponent } from './alarm-details/corrective-actions/edit-corrective-actions-modal/edit-corrective-actions-modal.component';
import { EditOperationsMaintenanceModalComponent } from './alarm-details/operations-maintenance/edit-operations-maintenance-modal/edit-operations-maintenance-modal.component';
import { NoDataComponent } from '../shared/components/no-data/no-data.component';
import { ErrorMessageComponent } from './alarm-details/error-message/error-message.component';
import { InteractiveTimeChartComponent } from '../shared/components/interactive-time-chart/interactive-time-chart.component';
import { CorrectiveActionsComponent } from './alarm-details/corrective-actions/corrective-actions.component';
import { OperationsMaintenanceComponent } from './alarm-details/operations-maintenance/operations-maintenance.component';
import { PotentialConsequenceComponent } from './alarm-details/potential-consequence/potential-consequence.component';
import { EditPotentialConsequencesModalComponent } from './alarm-details/potential-consequence/edit-potential-consequences-modal/edit-potential-consequences-modal.component';
import { CodeDisplayComponent } from '../shared/components/code-display/code-display.component';
import { LiveCodeDisplayComponent } from '../shared/components/live-code-display/live-code-display.component';
import { RuleDialogService } from '../shared/components/modify-rule/rule-dialog.service';
import { HealthIndicatorComponent } from './health-indicator/health-indicator.component';
import { AssetGroupComponent } from './health-indicator/asset-group/asset-group.component';
import { HealthIndicatorChartComponent } from './health-indicator/health-indicator-chart/health-indicator-chart.component';
import { StageDistributionChartComponent } from './factory-summary/stage-distribution-chart/stage-distribution-chart.component';
import { ActiveAlarmsCardComponent } from './factory-summary/active-alarms-card/active-alarms-card.component';
import { FailedRulesCardComponent } from './factory-summary/failed-rules-card/failed-rules-card.component';
import { HistoricalComponent } from './alarm-details/historical/historical.component';
import { WorkCenterViewComponent } from './work-center-view/work-center-view.component';
import { LabelManagementDialogComponent } from './work-center-view/label-management-dialog/label-management-dialog.component';
import { JournalDigestDialogComponent } from '../shared/components/journal-digest-dialog/journal-digest-dialog.component';
import { PlantFiltersComponent } from '../shared/components/plant-filters/plant-filters.component';
import { RuleCreationChatComponent } from '../shared/components/rule-creation-chat/rule-creation-chat.component';
import { RuleCreationChatPageComponent } from './rule-creation-chat-page/rule-creation-chat-page.component';
import { KpiDashboardComponent } from './kpi-dashboard/kpi-dashboard.component';
import { KpiFiltersComponent } from './kpi-dashboard/kpi-filters.component';
import { KpiStageDistributionComponent } from './kpi-dashboard/kpi-stage-distribution.component';
import { KpiAlarmCoverageComponent } from './kpi-dashboard/kpi-alarm-coverage.component';
import { KpiToleranceBreachComponent } from './kpi-dashboard/kpi-tolerance-breach.component';
import {
  HasRoleDirective,
  HasRolePipe,
  RoleDisabledDirective,
} from '@ind4.0/angular-common-lib';

@NgModule({
  declarations: [
    PredictiveMaintenanceComponent,
    RulesOverviewComponent,
    AlarmDetailsComponent,
    SedEventsSummaryComponent,
    SedEventsTableComponent,
    SedEventsDistributionComponent,
    SedEventsPredictiveMaintenanceComponent,
    SupportComponent,

    WelcomePageComponent,
    FloatingPathsComponent,
    EvaluationFormComponent,
    DescriptionComponent,
    CoordinatorComponent,
    NewModelComponent,
    EquipmentStructureImportComponent,
    MigrationImportComponent,
    GainParametersComponent,
    GainFormulaTemplatesComponent,
    FactorySummaryComponent,
    ModifyRuleComponent,
    LocalisationStepComponent,
    ParametersStepComponent,
    EquationsStepComponent,
    GainFormulaInstancesStepComponent,
    ThresholdsComponent,
    DataStateComponent,
    PotentialCauseComponent,
    EditPotentialCausesModalComponent,
    EditCorrectiveActionsModalComponent,
    EditOperationsMaintenanceModalComponent,
    NoDataComponent,
    ErrorMessageComponent,
    InteractiveTimeChartComponent,
    CorrectiveActionsComponent,
    OperationsMaintenanceComponent,
    PotentialConsequenceComponent,
    EditPotentialConsequencesModalComponent,
    CodeDisplayComponent,
    LiveCodeDisplayComponent,
    HealthIndicatorComponent,
    AssetGroupComponent,
    HealthIndicatorChartComponent,
    StageDistributionChartComponent,
    ActiveAlarmsCardComponent,
    FailedRulesCardComponent,
    HistoricalComponent,
    WorkCenterViewComponent,
    LabelManagementDialogComponent,
    JournalDigestDialogComponent,
    PlantFiltersComponent,
    RuleCreationChatComponent,
    RuleCreationChatPageComponent,
    KpiDashboardComponent,
    KpiFiltersComponent,
    KpiStageDistributionComponent,
    KpiAlarmCoverageComponent,
    KpiToleranceBreachComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FeaturesRoutingModule,
    SharedModule,
    SharedPrimengModule,
    BaseChartDirective,
    IconsModule,
    HasRoleDirective,
    RoleDisabledDirective,
    HasRolePipe,
  ],
  providers: [
    ConfirmationService,
    RuleDialogService,
    provideCharts(withDefaultRegisterables()),
  ],
})
export class FeaturesModule {}
